type ContentType =
  | "rss_feed"
  | "image"
  | "video"
  | "alert"
  | "notice"
  | "html"
  | "empty"
  | "loading";
const LAYOUT_NAMES = [
  "fullscreen",
  "landscape_weather_bottom",
  "kone_weather",
  "default",
] as const;
type LayoutName = (typeof LAYOUT_NAMES)[number];

type Provider = "citynews" | "sportsnet" | "verticalcity";

// Base interface for common content properties
interface BaseContent {
  content_type: ContentType;
  id: string;
  title_text?: string;
  body_text?: string;
  asset_url?: string;
  signature_url?: string;
  mime_type?: "image/jpeg" | "image/png" | "video/mp4" | "image" | "video";
  callback_url?: string;
  provider?: Provider; // e.g. "citynews" "sportsnet" have custom css (and slightly different html), ad providers have different callback requirements
  ad_id?: string | number; // identifies ad content (image/video); sent natively on v3, derived from content_source by the v2 adapter
  duration?: number; // spot duration in seconds; set by applyContentDefaults; consumed only by DataPipelinePlayEventPayload
  comment?: string; // not used by app, just for dev reference
}

// Extended interfaces for specific content types
interface RssFeedContent extends BaseContent {
  content_type: "rss_feed";
}

interface ImageContent extends BaseContent {
  content_type: "image";
  asset_url: string;
}

interface VideoContent extends BaseContent {
  content_type: "video";
  asset_url: string;
}

interface AlertContent extends BaseContent {
  content_type: "alert";
  title_text: string;
  body_text: string;
}

interface HtmlContent extends BaseContent {
  content_type: "html";
  asset_url: string; // NO STRING HTML - point to static hosted file
}

interface NoticeContent extends BaseContent {
  content_type: "notice";
}

interface EmptyContent extends BaseContent {
  content_type: "empty";
}

interface LoadingContent extends BaseContent {
  content_type: "loading";
}

interface WeatherContent extends ResponseWeather {
  // extends Omit<BaseContent, "content_type">, ResponseWeather {}
  // content_type?: "weather";
}

// Define the type for the contents array
type Content =
  | RssFeedContent
  | ImageContent
  | VideoContent
  | AlertContent
  | HtmlContent
  | NoticeContent
  | EmptyContent
  | LoadingContent;

// Define the type for spots array
interface Spot {
  duration: number;
  layout?: LayoutName; // optional, defaults to "fullscreen"
  fullscreen?: boolean; // instead of setting via layout, then central doesn't need to know about layouts at all
  contents: Content[];
}

interface ResponseWeather {
  content_type: "weather";
  unit?: "C" | "F"; // alt "celsius" | "fahrenheit", or "metric" | "imperial" (if we also want wind)
  current?: {
    current: number;
    feels_like: number;
    condition: string;
    condition_key?: string; // stable key for icon lookup, e.g. "sunny", "partly_cloudy_night"
    observed_at?: string; // ISO 8601 UTC timestamp, e.g. "2025-12-15T23:22:00Z"
  };
  forecast: {
    low: number;
    high: number;
    condition: string;
    condition_key?: string; // stable key for icon lookup, e.g. "sunny", "heavy_rain"
    date?: string; // YYYY-MM-DD, e.g. "2025-12-16"
  }[];
}

interface ResponseAdmin {
  force_reload_config?: boolean;
}

interface ContentApiResponse {
  ticker?: ResponseWeather[];
  spots: Spot[];
  admin?: ResponseAdmin;
}

// Type guards for specific content types
function isRssFeed(content: Content): content is RssFeedContent {
  return content.content_type === "rss_feed";
}

function isRssFeedContent(content: Content): content is RssFeedContent {
  return content.content_type === "rss_feed";
}

function isImageContent(content: Content): content is ImageContent {
  return content.content_type === "image";
}

function isVideoContent(content: Content): content is VideoContent {
  return content.content_type === "video";
}

function isAlertContent(content: Content): content is AlertContent {
  return content.content_type === "alert";
}

function isHtmlContent(content: Content): content is HtmlContent {
  return content.content_type === "html";
}

function isNoticeContent(content: Content): content is NoticeContent {
  return content.content_type === "notice";
}

function isEmptyContent(content: Content): content is EmptyContent {
  return content.content_type === "empty";
}

function isLoadingContent(content: Content): content is LoadingContent {
  return content.content_type === "loading";
}

function isAdContent(content: Content): content is ImageContent | VideoContent {
  return (
    (content.content_type === "image" || content.content_type === "video") &&
    !!content.ad_id
  );
}

function isValidLayoutName(layout: string): layout is LayoutName {
  // return layout === "fullscreen" || layout === "landscape_weather_bottom";
  return LAYOUT_NAMES.includes(layout as LayoutName);
}

export {
  isAdContent,
  isAlertContent,
  isNoticeContent,
  isEmptyContent,
  isHtmlContent,
  isImageContent,
  isLoadingContent,
  isRssFeed,
  isRssFeedContent,
  isValidLayoutName,
  isVideoContent,
};

export type {
  Content,
  ContentApiResponse,
  LayoutName,
  Provider,
  Spot,
  AlertContent,
  NoticeContent,
  EmptyContent,
  HtmlContent,
  ImageContent,
  LoadingContent,
  RssFeedContent,
  VideoContent,
  WeatherContent,
};
